import React, { Fragment, useEffect, useMemo } from 'react';
import { BufferGeometry, Matrix4 } from 'three';
import { mergeBufferGeometries } from 'three-stdlib';

import { isTruss, Truss, TrussEnvelope } from '@paragon/contract-library';
import { calculateMemberThreeJsGeometry } from '@paragon/three-shared';
import { groupByWithoutNullishKeys, notNullish } from '@paragon/utilities';

import { useAppSelector } from '../../../components/hooks';
import { calculateTrussPlyMatrices } from '../../../library/layout-geometry/truss-envelopes/calculateTrussPlyMatrices';
import { getCommittedComponentDesigns } from '../../../store/selectors/getComponentDesignsSelectors';
import { getVisibleDesignedEnvelopes } from '../../../store/selectors/layoutDataSelectors';
import { getAllTrussEnvelopePositions } from '../../../store/selectors/layoutDocumentSelectors';
import { is3DLayoutCameraInUse } from '../../../store/selectors/layoutModeSelectors';
import { getActiveEnvelopeMode } from '../../../store/selectors/layoutVisualOptionsSelectors';
import { LAYOUT_MEMBER_EDGE_COLOR } from '../../component/MemberVisual';
import { Instance, Instances } from '../Instances';
import { getEnvelopeColor } from '../truss-envelopes/colors';
import { getTrussEnvelopeStyles } from '../truss-envelopes/TrussEnvelopesController';
import { useTrussEnvelopeEventHandlers } from '../truss-envelopes/useTrussEnvelopeEventHandlers';
import { EnvelopeLodMonitor } from './EnvelopeLodMonitor';
import { LayoutComponentLabelsController } from './LayoutComponentLabelsController';
import { LayoutConnectingHardwareController } from './LayoutConnectingHardwareController';
import { LayoutPlatePairsVisual } from './LayoutPlatePairsVisual';

export function LayoutComponentDesignsController() {
  return (
    <>
      <EnvelopeLodMonitor />

      <LayoutPlatePairsVisual />
      <LayoutComponentMembersController />
      <LayoutConnectingHardwareController />
      <LayoutComponentLabelsController />
    </>
  );
}

function LayoutComponentMembersController() {
  const envelopeMode = useAppSelector(getActiveEnvelopeMode);
  const trussEnvelopes = useAppSelector(getVisibleDesignedEnvelopes);

  if (envelopeMode !== 'components') return null;

  return Object.entries(groupByWithoutNullishKeys(trussEnvelopes, (envelope) => envelope.componentDesignGuid)).map(
    ([componentDesignGuid, envelopesForDesign]) => (
      <SingleComponentLayoutMembersVisual
        key={componentDesignGuid}
        componentDesignGuid={componentDesignGuid}
        trussEnvelopes={envelopesForDesign}
      />
    ),
  );
}

const NEVER_RAYCAST = () => {};

function SingleComponentLayoutMembersVisual(props: { componentDesignGuid: string; trussEnvelopes: TrussEnvelope[] }) {
  const allEnvelopeMatrices = useAppSelector(getAllTrussEnvelopePositions);
  const component = useAppSelector(
    (state) =>
      getCommittedComponentDesigns(state).find((design) => design.guid === props.componentDesignGuid)?.component,
  );

  const geometry = useMemo(() => {
    if (!isTruss(component)) return null;

    const memberGeometries = component.members
      .map((member) => calculateMemberThreeJsGeometry(member, component))
      .filter(notNullish);

    if (memberGeometries.length === 0) return null;

    return mergeBufferGeometries(memberGeometries);
  }, [component]);

  useEffect(() => {
    if (geometry) {
      // grab a reference as `dispose` will be overwritten on the geometry due to `dispose={null}` below
      const { dispose } = geometry;
      return () => dispose.call(geometry);
    }
  }, [geometry]);

  if (!isTruss(component) || geometry == null) return null;

  return (
    <Instances geometry={geometry} initialSize={100} dispose={null}>
      <meshBasicMaterial />

      {props.trussEnvelopes
        .filter((envelope) => envelope.guid in allEnvelopeMatrices)
        .map((envelope) => (
          <SingleEnvelopeMembersInstance
            key={envelope.guid}
            component={component}
            envelope={envelope}
            matrix={allEnvelopeMatrices[envelope.guid]!}
            geometry={geometry}
          />
        ))}
    </Instances>
  );
}

function SingleEnvelopeMembersInstance(props: {
  component: Truss;
  envelope: TrussEnvelope;
  matrix: Matrix4;
  geometry: BufferGeometry;
}) {
  const envelopeStyles = useAppSelector(getTrussEnvelopeStyles);
  const showMemberEdges = useAppSelector(is3DLayoutCameraInUse);

  const eventHandlers = useTrussEnvelopeEventHandlers(props.envelope);

  const style = envelopeStyles[props.envelope.guid] ?? 'normal';
  const color = getEnvelopeColor(style);

  const plyMatrices = calculateTrussPlyMatrices(props.component, props.envelope, props.matrix);

  return plyMatrices.map((matrix, i) => (
    <Fragment key={i}>
      <Instance
        color={color}
        matrix={matrix}
        matrixWorld={matrix}
        matrixAutoUpdate={false}
        matrixWorldAutoUpdate={false}
        {...eventHandlers}
      />

      {showMemberEdges && (
        <lineSegments
          raycast={NEVER_RAYCAST}
          matrix={matrix}
          matrixWorld={matrix}
          matrixAutoUpdate={false}
          matrixWorldAutoUpdate={false}
        >
          <lineBasicMaterial color={LAYOUT_MEMBER_EDGE_COLOR} />
          <edgesGeometry args={[props.geometry]} />
        </lineSegments>
      )}
    </Fragment>
  ));
}
